import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import {
  UserIcon,
  HomeIcon,
  BuildingOfficeIcon,
  HomeModernIcon
} from '@heroicons/react/24/outline';
import NotificationBell from '../notifications/NotificationBell';
import MobileBottomNav from './MobileBottomNav';

const NAVIGATION = [
  { name: 'Home Page', href: '/', icon: HomeIcon },
  { name: 'Hotels', href: '/hotels', icon: BuildingOfficeIcon },
  { name: 'BnBs', href: '/bnbs', icon: HomeModernIcon },
];

const Header = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();

  // Handle click outside to close user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  // Close user menu when route changes
  useEffect(() => {
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
  };

  const navigation = NAVIGATION;

  const hasBnBRole = Boolean(user?.roles?.includes('bnb_host'));
  const hasHotelRole = Boolean(user?.roles?.includes('hotel_host'));
  const hasHostRoles = hasBnBRole || hasHotelRole;

  // A single "go to my dashboard" destination, computed purely from roles
  // held (not any "active" mode) -- both roles gets a chooser hub, a single
  // host role goes straight to that dashboard, no host role falls back to
  // the guest dashboard.
  const dashboardHref = hasBnBRole && hasHotelRole
    ? '/host/home'
    : hasHotelRole
      ? '/host-dashboard'
      : hasBnBRole
        ? '/host/bnb'
        : '/dashboard';
  const dashboardLabel = hasHostRoles ? 'Host Dashboard' : 'Dashboard';

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-medium border-b border-secondary-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 lg:h-20">
          {/* Logo: icon mark only on mobile to keep the top bar thin, full wordmark from lg up */}
          <Link to="/" className="flex items-center space-x-2">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2"
            >
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg lg:text-xl">M</span>
              </div>
              <span className="hidden lg:inline text-2xl font-bold text-gray-900">
                Msafiri <span className="text-red-600">Space</span>
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navigation.map((item) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className="relative group p-3 rounded-lg transition-all duration-200 text-gray-600 hover:text-red-600 hover:bg-gray-100"
              >
                <IconComponent className="w-6 h-6" />

                {/* Tooltip */}
                <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none bg-gray-900 text-white">
                  {item.name}
                  {/* Tooltip arrow */}
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-gray-900"></div>
                </div>
              </Link>
            );
          })}
        </nav>

          {/* Right side - Auth, etc. */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Become a Host Button: desktop only, mobile users find it via the profile menu */}
            {!hasHostRoles && (
              <Link
                to="/become-host"
                className="hidden lg:inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors duration-200 shadow-soft hover:shadow-medium"
              >
                Become a Host
              </Link>
            )}

            {/* Authenticated User Menu */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-1 sm:space-x-3">

                <NotificationBell isScrolled />

                {/* User Menu */}
                <div className="relative user-menu-container">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-secondary-100 transition-colors duration-200"
                  >
                    {user?.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt={user.first_name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-primary-600" />
                      </div>
                    )}
                    <span className="hidden sm:block text-sm font-medium text-secondary-700">
                      {user?.first_name || 'User'}
                    </span>
                  </motion.button>

                  {/* Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-large border border-secondary-100 py-2 z-50">
                    {!hasHostRoles && (
                      <Link
                        to="/become-host"
                        className="lg:hidden block px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors duration-200"
                      >
                        Become a Host
                      </Link>
                    )}
                    <Link
                      to={dashboardHref}
                      className="block px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors duration-200"
                    >
                      {dashboardLabel}
                    </Link>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors duration-200"
                    >
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-error-600 hover:bg-error-50 transition-colors duration-200"
                    >
                      Logout
                    </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Guest User Menu */
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium text-secondary-700 hover:text-primary-500 transition-colors duration-200"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors duration-200 shadow-soft hover:shadow-medium"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
    <MobileBottomNav />
    </>
  );
};

export default Header;
